// React imports
import { useState, useContext, useEffect } from "react";
import { useParams } from "@tanstack/react-router";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import useUser from "../../hooks/useUser";
import { useCheckAPI } from "../../hooks/useCheckAPI";
import { useStaffInfo } from "../../hooks/useStaffInfo";
// State Import
import { useDraftOrder } from "../../state/draftOrderState";
// Component imports
import { CustomStepper } from "../../components/CustomStepper";
// Page specific components
import { StepChanger } from "./StepChanger";
// import { DeliveryDetails } from "./DeliveryDetails";
import { ReviewOrder } from "./ReviewOrder";
import { SubmitOrder } from "./SubmitOrder";
import { CreateOrderBtn } from "./CreateOrderBtn";
import { StaffInfo } from "./StaffInfo";
// MUI imports
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ItemSelection } from "./ItemSelection";
import Stack from "@mui/material/Stack";

export function StaffRequest() {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const { user } = useUser();
  const staffID = user.staffID;
  const staffInfo = useStaffInfo(staffID);
  const contractCode = staffInfo.staff.contractcode;

  // Set steps and step state
  const steps = ["Create", "Review", "Submit"];
  const [activeStep, setActiveStep] = useState(0);

  const [items, setItems] = useState({ selected: [], core: [], accessory: [] });

  // Initialise order object
  const [order, setOrder] = useState({
    data: {
      filter: { noFilter: "" },
      order: {
        no: "",
        poNo: "",
        status: "",
        branch: "",
        requestedShippingDate: "",
        code: "",
        contact: "",
        name: "",
        address: "",
        address2: "",
        city: "",
        county: "",
        postCode: "",
        country: "",
        homeaddress: "",
        email: "",
        mobileNo: "",
        orderTotal: "",
      },
      staff: [{ no: "", staffLineNo: 0 }],
    },
  });

  // Update the order object poNo with the user's contract code
  useEffect(() => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      data: {
        ...prevOrder.data,
        order: {
          ...prevOrder.data.order,
          poNo: contractCode,
          email: "",
          mobileNo: "",
        },
        staff: prevOrder.data.staff.map((staff, index) =>
          index === 0
            ? { ...staff, no: staffID, staffpoNo: contractCode }
            : staff,
        ),
      },
    }));
  }, [contractCode]);

  // Get draft order no from url param
  // If no id then it is a new order
  const { id } = useParams({ strict: false });
  const getOrder = id ? true : false;

  // Fetch Draft Order data - only if an exisiting order
  const { isError, error, data, isSuccess } = useDraftOrder(id, getOrder);

  // Success Function
  const successFunc = () => {
    setOrder(data.data.data.data);
  };
  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });
  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  return (
    <Box>
      <Typography variant="h5" sx={{ pb: 2 }}>
        Staff Request{id && ` - ${id}`}
      </Typography>
      <CustomStepper steps={steps} activeStep={activeStep} />
      <StepChanger
        order={order}
        steps={steps}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
      />
      {isError && <Typography>Error: {error.message}</Typography>}

      {activeStep === 0 && (
        <>
          {getOrder === false ? (
            <Stack spacing={3}>
              <Box>
                <CreateOrderBtn order={order} staffInfo={staffInfo} />
              </Box>
              <StaffInfo staffInfo={staffInfo} />
            </Stack>
          ) : (
            <ItemSelection
              order={order}
              items={items}
              setItems={setItems}
              setOrder={setOrder}
              setActiveStep={setActiveStep}
            />
          )}
        </>
      )}

      {activeStep === 1 && (
        <ReviewOrder order={order} items={items} setItems={setItems} />
      )}

      {activeStep >= 2 && (
        <SubmitOrder order={order} setActiveStep={setActiveStep} />
      )}

      <StepChanger
        order={order}
        steps={steps}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
      />
    </Box>
  );
}
